import React from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, MessageCircle, Phone } from 'lucide-react';
import { usePublicLegalConfiguration } from '../platform/legalConfig';
import { getSafeLinkAttributes } from '../platform/safeLinks';

const SupportContactLinks: React.FC = () => {
  const { t } = useTranslation();
  const publicLegalConfiguration = usePublicLegalConfiguration();
  const publicContacts = publicLegalConfiguration.contacts;
  const supportEmail = publicContacts.support.email || publicContacts.supportEmail;
  const supportMailtoHref = publicContacts.support.mailtoHref || `mailto:${supportEmail}`;
  const publicPhoneDisplay = publicContacts.phonePublished ? publicContacts.phone : undefined;
  const supportPhoneDisplay = publicPhoneDisplay || undefined;
  const publicPhoneHref = publicContacts.phonePublished ? publicContacts.phoneHref : undefined;
  const configuredPhoneHref = publicPhoneHref || undefined;
  const publicWhatsappHref = publicContacts.whatsappEnabled ? publicContacts.whatsappUrl : undefined;
  const supportWhatsappHref = publicWhatsappHref || undefined;

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <a
        href={supportMailtoHref}
        aria-label={`${t('email')}: ${supportEmail}`}
        title={supportEmail}
        className="group flex flex-col items-center rounded-lg border border-sky-200 bg-gradient-to-br from-blue-50 to-sky-50 p-4 text-center transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2"
      >
        <Mail aria-hidden="true" className="mb-2 h-6 w-6 text-sky-600 transition-transform group-hover:scale-110" />
        <span className="text-xs font-medium text-slate-700">{t('email')}</span>
        <span className="mt-1 break-all text-center text-xs text-slate-600">{supportEmail}</span>
      </a>

      {supportPhoneDisplay && configuredPhoneHref ? (
        <a
          href={configuredPhoneHref}
          aria-label={`${t('phone')}: ${supportPhoneDisplay}`}
          title={supportPhoneDisplay}
          className="group flex flex-col items-center rounded-lg border border-green-200 bg-gradient-to-br from-emerald-50 to-green-50 p-4 text-center transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400 focus-visible:ring-offset-2"
        >
          <Phone aria-hidden="true" className="mb-2 h-6 w-6 text-green-600 transition-transform group-hover:scale-110" />
          <span className="text-xs font-medium text-slate-700">{t('phone')}</span>
          <span className="mt-1 text-center text-xs text-slate-600">{supportPhoneDisplay}</span>
        </a>
      ) : null}

      {supportWhatsappHref ? (
        <a
          href={supportWhatsappHref}
          {...getSafeLinkAttributes(supportWhatsappHref, 'external')}
          aria-label={`${t('whatsapp')}: ${t('errorDirectChat')}`}
          title={t('errorDirectChat')}
          className="group flex flex-col items-center rounded-lg border border-action-200 bg-gradient-to-br from-action-50 to-white p-4 text-center transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action-700 focus-visible:ring-offset-2"
        >
          <MessageCircle aria-hidden="true" className="mb-2 h-6 w-6 text-action-600 transition-transform group-hover:scale-110" />
          <span className="text-xs font-medium text-slate-700">{t('whatsapp')}</span>
          <span className="mt-1 text-center text-xs text-slate-600">{t('errorDirectChat')}</span>
        </a>
      ) : null}
    </div>
  );
};

export default SupportContactLinks;
